/* eslint-disable jsdoc/require-description-complete-sentence */
import { useLocation } from 'react-router-dom';
import qs from 'query-string';

import useIsNativeWebView from '../../native-bridge/hooks/useIsWebView';

/**
 * Determines whether native apps (iOS / Android) support certain features
 * on the AccountCard configurator so that they can take over control.
 *
 * Native app feature supports come in from a URL encoded query param "native_features" for example:
 * /cards/create/native_features=%7B"card_element_types_configuration"%3A%5B"ADDRESS"%2C"TEXT"%5D%7D
 * which translates to the following search query / JSON:
 *
 * "native_features": {
 *   "card_element_types_configuration": ["ADDRESS", "TEXT", "PHOTO"]
 * }
 *
 * @returns {{nativeSupportsCardElementConfiguration: (function(CARD_ELEMENT_TYPE): boolean)}}
 */
const useAccountCardNativeFeatureSupport = () => {
  const location = useLocation();
  const query = qs.parse(location?.search);
  const nativeFeatures = JSON.parse(query?.native_features ?? '{}');
  const isNativeWebView = useIsNativeWebView();

  /**
   * Whether a CardElement can be configured by native app controls.
   * Always returns false if we're not in a webview.
   *
   * @param {CARD_ELEMENT_TYPE} cardElementType The CardElement type.
   * @returns {boolean}
   */
  const nativeSupportsCardElementConfiguration = cardElementType => {
    if (
      !nativeFeatures?.card_element_types_configuration?.length ||
      !isNativeWebView
    ) {
      return false;
    }

    return !!nativeFeatures.card_element_types_configuration?.includes(
      cardElementType,
    );
  };

  return {
    nativeSupportsCardElementConfiguration,
  };
};

export default useAccountCardNativeFeatureSupport;
